/**
 * Centralized logging utility with environment-based control
 *
 * Console output only — telemetry is handled by Faro (src/boot/faro.ts).
 *
 * Usage:
 *   import { logger } from 'src/utils/logger';
 *   logger.debug('[Component]', 'Debug message');
 *   logger.info('[Component]', 'Info message');
 *   logger.warn('[Component]', 'Warning message');
 *   logger.error('[Component]', 'Error message');
 *
 * Environment variables:
 *   VITE_ENABLE_DEBUG_LOGS=true     - Enable debug logs
 *   VITE_ENABLE_INFO_LOGS=true      - Enable info logs (default: true in dev)
 *   VITE_LOG_LEVEL=debug|info|warn|error - Set log level
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

interface LogLevelMap {
  [key: string]: number;
  debug: number;
  info: number;
  warn: number;
  error: number;
  silent: number;
}

const isDev: boolean = process.env.NODE_ENV === 'development';

const enableDebug: boolean = process.env.VITE_ENABLE_DEBUG_LOGS === 'true';
const enableInfo: boolean =
  process.env.VITE_ENABLE_INFO_LOGS === 'true' || isDev;
const logLevel: string =
  process.env.VITE_LOG_LEVEL || (isDev ? 'debug' : 'warn');

const levels: LogLevelMap = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  silent: 4,
};

const currentLevel: number = levels[logLevel] ?? levels.warn;

interface Logger {
  debug(...args: any[]): void;
  info(...args: any[]): void;
  warn(...args: any[]): void;
  error(...args: any[]): void;
  language(...args: any[]): void;
  router(...args: any[]): void;
  tolgee(...args: any[]): void;
}

export const logger: Logger = {
  debug(...args: any[]): void {
    if (enableDebug || currentLevel <= levels.debug) {
      console.log(...args);
    }
  },

  info(...args: any[]): void {
    if (enableInfo || currentLevel <= levels.info) {
      console.info(...args);
    }
  },

  warn(...args: any[]): void {
    if (currentLevel <= levels.warn) {
      console.warn(...args);
    }
  },

  error(...args: any[]): void {
    if (currentLevel <= levels.error) {
      console.error(...args);
    }
  },

  language(...args: any[]): void {
    if (process.env.VITE_LOG_LANGUAGE === 'true' || enableDebug) {
      console.log('[LANGUAGE]', ...args);
    }
  },

  router(...args: any[]): void {
    if (process.env.VITE_LOG_ROUTER === 'true' || enableDebug) {
      console.log('[ROUTER]', ...args);
    }
  },

  tolgee(...args: any[]): void {
    if (process.env.VITE_LOG_TOLGEE === 'true' || enableDebug) {
      console.log('[TOLGEE]', ...args);
    }
  },
};

/**
 * Factory function to create a scoped logger with component name prefix
 * Usage: const logger = useLogger('ComponentName');
 */
export function useLogger(componentName: string): Logger {
  return {
    debug(...args: any[]): void {
      if (enableDebug || currentLevel <= levels.debug) {
        console.log(`[${componentName}]`, ...args);
      }
    },

    info(...args: any[]): void {
      if (enableInfo || currentLevel <= levels.info) {
        console.info(`[${componentName}]`, ...args);
      }
    },

    warn(...args: any[]): void {
      if (currentLevel <= levels.warn) {
        console.warn(`[${componentName}]`, ...args);
      }
    },

    error(...args: any[]): void {
      if (currentLevel <= levels.error) {
        console.error(`[${componentName}]`, ...args);
      }
    },

    language(...args: any[]): void {
      if (process.env.VITE_LOG_LANGUAGE === 'true' || enableDebug) {
        console.log(`[${componentName}] [LANGUAGE]`, ...args);
      }
    },

    router(...args: any[]): void {
      if (process.env.VITE_LOG_ROUTER === 'true' || enableDebug) {
        console.log(`[${componentName}] [ROUTER]`, ...args);
      }
    },

    tolgee(...args: any[]): void {
      if (process.env.VITE_LOG_TOLGEE === 'true' || enableDebug) {
        console.log(`[${componentName}] [TOLGEE]`, ...args);
      }
    },
  };
}

export default logger;
